import { AlertTriangle, Check, Clock, Pause } from 'lucide-react';
import type { ReactNode } from 'react';

import type { SlaState } from '@beacon/core';
import { cn } from '@/lib/cn.js';

interface Cfg {
  tone: 'green' | 'slate' | 'yellow' | 'red';
  icon: ReactNode;
  prefix: string | null;
}

const config: Record<SlaState, Cfg> = {
  met: { tone: 'green', icon: <Check size={12} />, prefix: 'Met' },
  ok: { tone: 'slate', icon: <Clock size={12} />, prefix: null },
  warn: { tone: 'yellow', icon: <Clock size={12} />, prefix: null },
  breaching: { tone: 'red', icon: <AlertTriangle size={12} />, prefix: null },
  breached: { tone: 'red', icon: <AlertTriangle size={12} />, prefix: 'Breached' },
  paused: { tone: 'slate', icon: <Pause size={12} />, prefix: 'Paused' },
};

const toneClasses = {
  green: 'bg-ok-500/10 text-ok-400 border-ok-500/30',
  slate: 'bg-ink-700 text-ink-200 border-ink-600',
  yellow: 'bg-warn-500/15 text-warn-400 border-warn-500/30',
  red: 'bg-danger-500/15 text-danger-400 border-danger-500/30',
};

/**
 * SLA countdown pill — state-driven visual treatment.
 *
 * The live timer must update via subscription/diff, never a naive
 * re-render (INTERACTION_SPEC §4). Animate value transitions over
 * 200 ms. Never blink.
 */
export function SLA({
  state,
  label,
}: {
  state: SlaState;
  label?: string;
}) {
  const cfg = config[state];
  return (
    <span
      className={cn(
        // 200ms tone transition per INTERACTION_SPEC §5 — SLA state
        // crossings (ok→warn→breaching, paused/resumed) shouldn't snap.
        'inline-flex items-center gap-1 mono text-[11px] px-1.5 h-[20px] rounded-[5px] border leading-none transition-colors duration-200',
        toneClasses[cfg.tone]
      )}
    >
      {cfg.icon}
      <span>
        {cfg.prefix
          ? `${cfg.prefix}${label ? ` · ${label}` : ''}`
          : label ?? ''}
      </span>
    </span>
  );
}
